import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';

export interface DetailTab {
  id: string;
  label: string;
}

interface DetailPageLayoutProps {
  backTo: string;
  backLabel: string;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  tabs: DetailTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  isLoading?: boolean;
  notFound?: boolean;
  notFoundMessage?: string;
  children: ReactNode;
}

export function DetailPageLayout({
  backTo,
  backLabel,
  title,
  subtitle,
  actions,
  tabs,
  activeTab,
  onTabChange,
  isLoading,
  notFound,
  notFoundMessage = 'This item could not be found.',
  children,
}: DetailPageLayoutProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-gray-600 dark:text-warm-sand">
        <Loader2 className="w-8 h-8 animate-spin text-amber-450 mb-3" />
        <span>Loading details…</span>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-4">
        <Link
          to={backTo}
          className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-warm-sand hover:text-amber-450"
        >
          <ArrowLeft className="w-4 h-4" />
          {backLabel}
        </Link>
        <div className="card p-8 text-center">
          <p className="text-gray-900 dark:text-warm-cream font-medium">{notFoundMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Link
        to={backTo}
        className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-warm-sand hover:text-amber-450"
      >
        <ArrowLeft className="w-4 h-4" />
        {backLabel}
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-cream">{title}</h1>
          {subtitle && <p className="text-gray-600 dark:text-warm-sand mt-1">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>

      <div className="border-b border-gray-200 dark:border-charcoal-700 overflow-x-auto">
        <nav className="flex gap-1 min-w-max">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-amber-450 text-amber-450'
                  : 'border-transparent text-gray-600 dark:text-warm-sand hover:text-gray-900 dark:hover:text-warm-cream'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div>{children}</div>
    </div>
  );
}
